import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { computed, inject, ref } from 'vue'
import type { Blob as ActiveBlob } from '@rails/activestorage'
import debounce from 'lodash/debounce'

import { createAbortReason } from '@app/adapters/abort'
import { SfLog } from '@app/adapters/logger'
import { DocumentLinkType } from '@app/shared/types/sf-data'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type {
  ExternalNoteUploaderItem,
  PreparePageData,
} from '@app/application/client_meetings/types'
import * as ExternalNotesApi from '@app/application/client_meetings/api/external-notes'
import * as MeetingsApi from '@app/application/client_meetings/api/meetings'
import { formatExistingDocument } from '../utils'

const PASTED_NOTES_DEBOUNCE_MS = 2000

type PendingUpload = {
  uuid: string
  document: ExternalNoteUploaderItem
  progress: string
}

export const useExternalNoteStore = defineStore('externalNote', () => {
  const I18n = useI18n()
  const pageData = inject('pageData') as PreparePageData

  const error: Ref<string | null> = ref(null)
  const documents: Ref<ExternalNoteUploaderItem[]> = ref(
    pageData.externalNotes.map(formatExistingDocument)
  )
  const pendingUploads: Ref<PendingUpload[]> = ref([])
  const maxExternalNotes = pageData.maxExternalNotes

  const pastedNotes: Ref<string> = ref(pageData.pastedNotes)
  let pastedNotesController: AbortController | null = null

  const atLimit = computed(
    () =>
      documents.value.length + pendingUploads.value.length >= maxExternalNotes
  )

  function fileInvalid(): void {
    error.value = I18n.t(
      'client_meetings.prepare.external_note_modal.upload.invalid_file'
    )
  }

  function fileAdded({ file, uuid }: { file: File; uuid: string }): void {
    if (atLimit.value) {
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.limit_reached',
        { count: maxExternalNotes }
      )
      return
    }
    pendingUploads.value.push({
      uuid,
      progress: '0',
      document: {
        linkType: DocumentLinkType.DirectUpload,
        link: '',
        name: file.name,
        contentType: file.type,
        size: file.size,
        unique_id: uuid,
      },
    })
    error.value = null
  }

  function fileProgress({
    progress,
    uuid,
  }: {
    progress: string
    uuid: string
  }): void {
    const pending = pendingUploads.value.find((p) => p.uuid === uuid)
    if (pending) {
      pending.progress = progress
    }
  }

  async function fileUploaded({
    blob,
    uuid,
  }: {
    blob: ActiveBlob
    uuid: string
  }): Promise<void> {
    const pending = pendingUploads.value.find((p) => p.uuid === uuid)
    if (!pending) return
    pending.progress = '100'

    try {
      const response = await ExternalNotesApi.attach(blob.signed_id)
      if (response) {
        documents.value.push(formatExistingDocument(response.document))
      } else {
        SfLog.error('Got empty response when calling attachExternalNote')
      }
    } catch {
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.attach_error'
      )
    } finally {
      pendingUploads.value = pendingUploads.value.filter((p) => p.uuid !== uuid)
    }
  }

  async function deleteExternalNote(
    document: ExternalNoteUploaderItem
  ): Promise<void> {
    if (document.linkType !== DocumentLinkType.Document) {
      return
    }

    try {
      await ExternalNotesApi.delete(document.document_id)
      documents.value = documents.value.filter(
        (d) => d.unique_id !== document.unique_id
      )
      error.value = null
    } catch {
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.delete_error'
      )
    }
  }

  const debouncedSavePastedNotes = debounce(async () => {
    const controller = new AbortController()
    pastedNotesController = controller

    try {
      await MeetingsApi.update({
        id: pageData.clientMeetingId,
        pastedNotes: pastedNotes.value,
        signal: controller.signal,
      })
      error.value = null
    } catch {
      if (controller.signal.aborted) return
      error.value = I18n.t(
        'client_meetings.prepare.external_note_modal.pasted_notes_save_error'
      )
    } finally {
      if (pastedNotesController === controller) {
        pastedNotesController = null
      }
    }
  }, PASTED_NOTES_DEBOUNCE_MS)

  function setPastedNotes(text: string): void {
    if (text === pastedNotes.value) return

    pastedNotesController?.abort(createAbortReason('Superseded by newer edit'))
    pastedNotes.value = text
    void debouncedSavePastedNotes()
  }

  function flushPastedNotesSave(): void {
    void debouncedSavePastedNotes.flush()
  }

  return {
    documents,
    pendingUploads,
    error,
    atLimit,
    fileInvalid,
    fileAdded,
    fileProgress,
    fileUploaded,
    deleteExternalNote,
    pastedNotes,
    setPastedNotes,
    flushPastedNotesSave,
  }
})
